import { ArrowDownIcon, ExclamationCircleIcon, FilterIcon, RefreshIcon, XIcon } from "@heroicons/react/outline";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { KeyedMutator } from "swr";
import useDebounce from "../hooks/useDebounce";
import useUser from "../hooks/useUser";
import useWorkflowAutomationItemsFiltered from "../hooks/useWorkflowAutomationItemsFiltered";
import { OutputFilters, SortBy, Workflow } from "../interfaces";
import { isItemActionable } from "../utils";
import Paginator from "./Paginator";
import WorkflowOutputsFilter from "./WorkflowOutputsFilter";
import WorkflowOutputsItem from "./WorkflowOutputsItem";

interface Props {
  workflow?: Workflow;
  mutateWorkflow?: KeyedMutator<Workflow>;
}

const initialFilters: OutputFilters = { from: '', to: '', status: '', filename: '' };

function WorkflowOutputs(props: Props) {
  const { user } = useUser();
  const { workflow, mutateWorkflow } = props;

  const history = useHistory();

  const { t } = useTranslation();

  const { pathname, search } = useLocation();
  const { url } = useRouteMatch();

  const searchParams = new URLSearchParams(search);

  const automationId = searchParams.get("automation");

  const [sortBy, setSortBy] = useState<SortBy>({ field: 'date', direction: 'descending' });

  const [currentPageNumber, setCurrentPageNumber] = useState<number>(1);

  const [filters, setFilters] = useState<OutputFilters>({ ...initialFilters });
  const [displayFilters, setDisplayFilters] = useState(false);
  const debouncedFilters = useDebounce(filters, 500);

  const { data, mutate, isValidating } = useWorkflowAutomationItemsFiltered(workflow?.id, displayFilters ? debouncedFilters : {}, currentPageNumber, sortBy, automationId);
  const { results: items, numPages } = data;

  const [isRefreshing, setIsRefreshing] = useState(false);

  const [previousLastViewedTime, setPreviousLastViewedTime] = useState<number>(0);

  const allItemIds = useMemo(() => (items ?? []).map(i => i.id), [items]);
  const actionableItemIds = useMemo(
    () => (items ?? []).filter(i => isItemActionable(i, user?.id ?? '', workflow?.review ?? undefined)).map(i => i.id),
    [items, user?.id, workflow?.review]
  );

  const enabledPrevious = currentPageNumber > 1;
  const enabledNext = currentPageNumber < numPages;

  useEffect(() => {
    setCurrentPageNumber(1);
  }, [automationId])

  function handleClickRefresh() {
    setIsRefreshing(true);
    mutate()
      .finally(() => setIsRefreshing(false));

    if (!workflow?.outputFolder && mutateWorkflow)
      mutateWorkflow();
  }

  function onPageChange(newPage: number) {
    setCurrentPageNumber(newPage);
    mutate();
  }

  function handleSortButton(field: string) {
    const direction = sortBy.field === field && sortBy.direction === "descending" ? "ascending" : "descending";
    setSortBy({ field: field, direction });
  }

  const areFiltersApplied = useMemo(() => {
    let hasFilterApplied = false;
    Object.keys(filters).forEach(key => {
      const keyValue = key as keyof typeof filters;
      if (filters[keyValue] !== initialFilters[keyValue])
        hasFilterApplied = true;
    })

    return hasFilterApplied
  }, [filters])

  useEffect(() => {
    if (areFiltersApplied && filters.filename.length > 0)
      setSortBy({ field: "filename", direction: "ascending" })
  }, [areFiltersApplied, filters]);

  useEffect(() => {
    if (!pathname.endsWith("/outputs"))
      return;

    const key = "lastViewedOuputsMap";
    const lastViewedOuputsMapString = localStorage.getItem(key) ?? "{}";
    const lastViewedOuputsMap: Record<string, number> = JSON.parse(lastViewedOuputsMapString);

    const workflowKey = workflow?.id ?? "__global";

    setPreviousLastViewedTime(lastViewedOuputsMap[workflowKey] ?? 0)

    lastViewedOuputsMap[workflowKey] = new Date().getTime();
    localStorage.setItem(key, JSON.stringify(lastViewedOuputsMap));

    if (!workflow?.outputFolder && mutateWorkflow)
      mutateWorkflow();

    if (workflow)
      mutate();

  }, [workflow, mutateWorkflow, mutate, pathname])

  return (
    <div className="border border-gray-200 rounded-lg bg-white p-4 w-full">
      <div className="flex items-center gap-2 w-full mb-4">
        <div className="font-gilroy font-semibold text-gray-600 text-lg">
          {t("Workflow Outputs")}
        </div>
        <button className="flex items-center gap-1 font-semibold font-gilroy text-gray-600 hover:text-blue hover:underline cursor-pointer" onClick={handleClickRefresh}>
          <RefreshIcon className={`h-5 w-5 ${(isRefreshing || isValidating) ? "animate-spin" : "animate-none"}`} />
        </button>

        {automationId &&
          <div className="flex gap-1 ml-auto font-gilroy font-semibold text-gray-600 text-sm items-center">
            <ExclamationCircleIcon className="h-6 w-6 flex-shrink-0 text-blue" />
            {t("Filtering outputs for selected automation")},
            <Link to={url + "/outputs"} className="underline hover:text-blue">{t("show all")}</Link>
          </div>
        }
        {workflow &&
          <XIcon className="w-6 h-6 ml-auto text-gray-400 hover:text-black cursor-pointer" onClick={() => history.push(url)} />
        }
      </div>

      <div className="flex items-center font-semibold text-xs text-gray-400 w-full overflow-y-scroll scrollbar-visible">
        <div className="w-4 flex-shrink-0" />{/* Indicator */}

        <div className="flex w-full items-center min-w-[300px]">{/* Filename column */}
          <button
            className={`flex items-center relative gap-0.5 font-semibold font-gilroy text-sm hover:text-blue hover:underline cursor-pointer ${sortBy.field === 'filename' ? 'text-blue' : 'text-gray-600'} `}
            onClick={() => handleSortButton("filename")}
          >
            {t("Filename")}
            <ArrowDownIcon className={`h-3 w-3 transition-all ${sortBy.field === 'filename' && sortBy.direction === 'ascending' ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <div className="w-60 flex-shrink-0"> {/* Workflow/User column */}
          <div className="font-semibold font-gilroy text-sm text-gray-600 text-left">
            {pathname === "/outputs" && "Workflow"}
          </div>
        </div>

        <div className="w-40 flex-shrink-0 flex justify-center"> {/* Status column */}
          <button
            className={`flex justify-center items-center relative gap-0.5 font-semibold font-gilroy text-sm hover:text-blue hover:underline cursor-pointer ${sortBy.field === 'status' ? 'text-blue' : 'text-gray-600'} `}
            onClick={() => handleSortButton("status")}
          >
            {t("Status")}
            <ArrowDownIcon className={`h-3 w-3 transition-all ${sortBy.field === 'status' && sortBy.direction === 'ascending' ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <div className="w-40 flex-shrink-0 flex justify-center"> {/* Created column */}
          <button
            className={`flex justify-center items-center relative gap-0.5 font-semibold font-gilroy text-sm hover:text-blue hover:underline cursor-pointer ${sortBy.field === 'date' ? 'text-blue' : 'text-gray-600'} `}
            onClick={() => handleSortButton("date")}
          >
            {t("Created")}
            <ArrowDownIcon className={`h-3 w-3 transition-all ${sortBy.field === 'date' && sortBy.direction === 'ascending' ? 'rotate-180' : ''}`} />
          </button>
        </div>

        <div className="flex justify-center w-10 flex-shrink-0"> {/* Filter/Action column */}
          <button
            className="text-gray-600 hover:text-blue hover:underline cursor-pointer"
            onClick={() => setDisplayFilters(prev => !prev)}
          >
            <FilterIcon className="h-5 w-5" />
          </button>
        </div>

      </div>

      {displayFilters &&
        <WorkflowOutputsFilter
          filters={filters}
          setFilters={setFilters}
          areFiltersApplied={areFiltersApplied}
        />
      }

      <div className="h-full overflow-y-scroll scrollbar-visible mb-4 border-t-2 border-b-2" style={{ "height": `calc(100vh - 240px - ${displayFilters ? "60px" : "0px"})` }}>

        {(items === undefined && isValidating) &&
          <div className="flex flex-col justify-center items-center gap-2 w-full h-full">
            <img src="https://static.portant.co/portant-loading-gray.svg" className="w-24 h-24" alt="Loading Logo" />
            <div className="flex font-gilroy font-semibold text-gray-400">
              {t("Loading workflow outputs...")}
            </div>
          </div>
        }

        {(items === undefined && !isValidating) &&
          <div className="flex flex-col justify-center items-center gap-2 w-full h-full">
            <div className="flex font-gilroy font-semibold text-gray-400">
              {t("Unable to load items")}
            </div>
          </div>
        }

        {(items !== undefined && items.length === 0) &&
          <div className="flex flex-col justify-center items-center gap-1 w-full h-full">
            <div className="flex font-gilroy text-gray-600 font-semibold">
              {areFiltersApplied ? t("No outputs match the filters") : t("No outputs have been created yet")}
            </div>
            <div className="text-sm">
              {areFiltersApplied ? t("Please try removing the filters") : t("Please run an automation to create your first output")}
            </div>
          </div>
        }

        {(items !== undefined && items.length > 0) &&
          <div className="w-full">
            {items.map(item =>
              <WorkflowOutputsItem key={item.id} item={item} lastViewedTime={previousLastViewedTime} showWorkflow={pathname === "/outputs"} refreshOuputs={mutate} allItemIds={allItemIds} actionableItemIds={actionableItemIds} />
            )}
          </div>
        }
      </div>

      <Paginator
        enabledNext={enabledNext}
        enabledPrevious={enabledPrevious}
        onPageChange={onPageChange}
        pageNumber={currentPageNumber}
        numPages={numPages}
      />
    </div>
  );
}

export default WorkflowOutputs;
